import React from "react";
import Button from "./Button";
import { X } from "lucide-react";
import {
  successApiIcon,
  disabledMailIcon,
  successPopupIcon,
  failedApiIcon,
  failurePopupIcon,
} from "./svg";
// import { LoaderCircle } from "lucide-react";
import { LoaderCircle } from "lucide-react";
import ProgressBar from "./ProgressBar";
import { API_STATUS } from "./constants";

export const Modal = ({
  isOpen,
  header,
  message,
  buttonTitle,
  buttonAction,
  isCross = false,
  crossAction,
  isLoading = false,
  isSuccess = false,
  successHeader,
  successMessage,
  successButton,
  successButtonAction,
  loadingMessage,
  milestoneName,
  submitStatus,
  emailStatus,
  submitAttempts,
  emailAttempts,
  closeModal,
  milestoneNo,
  milestoneDescription,
  retrySubmission,
  moveToNextMilestone,
  MAX_RETRIES = 3,
  isCooldownActive,
  cooldownTime,
  hasInput,
  inputComponent,
  isFailure = false,
  failureHeader,
  failureMessage,
  failureButton,
  failureButtonAction,
  isButtonDisabled = false,
}) => {
  if (!isOpen) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case API_STATUS.InProgress:
        return (
          <div className="h-7 w-7 bg-primary-50 rounded-full flex justify-center items-center">
            <LoaderCircle className="animate-spin text-primary-500 h-4 w-4" />
          </div>
        );
      case API_STATUS.Success:
        return (
          <div className="h-7 w-7 bg-green-100 rounded-full flex justify-center items-center ">
            {successApiIcon()}
          </div>
        );
      case API_STATUS.Failed:
        return (
          <div className="h-7 w-7 bg-red-25 rounded-full flex justify-center items-center ">
            {failedApiIcon()}
          </div>
        );
      default:
        return (
          <div className="h-7 w-7 bg-gray-200 rounded-full flex justify-center items-center">
            {disabledMailIcon()}
          </div>
        ); // Disabled state
    }
  };

  return (
    <div className="fixed inset-0 bg-black-700 bg-opacity-40 flex items-center justify-center p-4">
      <div
        className={
          isLoading
            ? "bg-white rounded-lg shadow-xl max-w-md"
            : `bg-white rounded-lg shadow-xl max-w-md w-full`
        }
      >
        {isLoading ? (
          submitStatus ? (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <h2 className="text-lg text-gray-500 font-bold text-left relative">
                  {milestoneName ? milestoneName : `Milestone ${milestoneNo}`}
                </h2>
                <div className="text-sm text-gray-400">
                  {milestoneDescription}
                </div>

                <div className="relative py-3">
                  <div className="absolute h-8 w-[1px] bg-gray-300 top-[3.75rem] left-[0.85rem] "></div>
                  {/* Step 1: Updating Details */}
                  <div className="flex items-center gap-3 mt-4 mb-6">
                    {getStatusIcon(submitStatus)}
                    <div>
                      <p
                        className={`text-sm font-semibold
                           ${
                             submitStatus === "in-progress"
                               ? "text-primary-500"
                               : submitStatus === API_STATUS.Failed
                                 ? "text-red-500"
                                 : "text-gray-500"
                           }
                        `}
                      >
                        Updating Details
                      </p>
                      <p className="text-xs text-gray-400">
                        Syncing Milestone Status
                      </p>
                    </div>
                  </div>

                  {/* Step 2: Sending Email */}
                  <div className="flex items-center gap-3 mt-4">
                    {getStatusIcon(emailStatus)}
                    <div>
                      <p
                        className={`text-sm font-semibold ${
                          emailStatus === "in-progress"
                            ? "text-primary-500"
                            : emailStatus === API_STATUS.Failed
                              ? "text-red-500"
                              : "text-gray-500"
                        }`}
                      >
                        Sending Update
                      </p>
                      <p className="text-xs text-gray-400">
                        Sending Email Updates to Stakeholders
                      </p>
                    </div>
                  </div>
                </div>

                {/* Retry Button (Only if one or both APIs fail) */}

                {submitStatus === API_STATUS.Failed && (
                  <Button
                    text={"Retry Submission"}
                    size={"md"}
                    onClick={() => retrySubmission("submit")} // ✅ Only retries milestone update
                    disabled={isCooldownActive}
                  />
                )}

                {submitStatus === API_STATUS.Success &&
                  submitAttempts > 0 &&
                  emailStatus === "disabled" && (
                    <Button
                      text={"Retry Sending Email"}
                      onClick={() => retrySubmission("email")}
                      disabled={isCooldownActive}
                      size={"md"}
                    ></Button>
                  )}
                {emailStatus === API_STATUS.Failed && (
                  <Button
                    text={"Retry Sending Email"}
                    onClick={() => retrySubmission("email")}
                    disabled={isCooldownActive}
                    size={"md"}
                  ></Button>
                )}

                {(submitAttempts > 0 || emailAttempts > 0) && (
                  <div className="flex justify-between">
                    <p className="text-xs text-gray-400 py-2">
                      Attempt{" "}
                      {submitStatus === API_STATUS.Success
                        ? emailAttempts
                        : submitAttempts}{" "}
                      of {MAX_RETRIES}
                    </p>

                    <div className="w-1/2  text-white">
                      <ProgressBar
                        currentStep={
                          submitStatus === API_STATUS.Success
                            ? emailAttempts
                            : submitAttempts
                        }
                        totalSteps={MAX_RETRIES}
                      />
                    </div>
                  </div>
                )}
                {isCooldownActive && (
                  <p className="text-gray-500 text-xs ">
                    Retry in {Math.floor(cooldownTime / 60)}:
                    {cooldownTime % 60 < 10
                      ? `0${cooldownTime % 60}`
                      : cooldownTime % 60}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="w-[240px] min-h-[130px] flex flex-col justify-around items-center p-4">
              <div>
                <LoaderCircle className="animate-spin h-10 w-10 text-primary-500" />
              </div>
              <p className="text-sm text-gray-500 font-semibold text-center">
                {loadingMessage}
              </p>
            </div>
          )
        ) : isSuccess ? (
          <div className="p-6">
            <div className="flex justify-center  mb-4">
              {successPopupIcon()}
            </div>
            <div className="flex justify-center  mb-4">
              <h2 className="text-lg text-gray-500 font-bold text-center">
                {successHeader}
              </h2>
            </div>
            <p className="text-sm text-gray-400 mb-6 text-center">
              {successMessage}
            </p>
            <Button
              text={successButton}
              size={"md"}
              onClick={successButtonAction}
            />
          </div>
        ) : isFailure ? (
          <div className="p-6">
            <div className="flex justify-between  mb-4">
              <div className={`${isCross ? "w-6" : ""}`}></div>
              {failurePopupIcon()}
              <div>
                {isCross ? (
                  <button onClick={crossAction}>
                    <X />
                  </button>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div className="flex justify-center  mb-4">
              <h2 className="text-lg text-gray-500 font-bold text-center">
                {failureHeader}
              </h2>
            </div>
            <p className="text-sm text-gray-400 mb-6 text-center">
              {failureMessage}
            </p>
            <Button
              text={failureButton}
              size={"md"}
              onClick={failureButtonAction}
            />
          </div>
        ) : (
          <div className="p-6">
            <div className="flex justify-between  mb-4">
              <div className="w-7"></div>
              <div className="text-lg text-gray-500 font-bold text-center grow">
                {header}
              </div>

              {isCross ? (
                <div>
                  <button onClick={crossAction}>
                    <X />
                  </button>
                </div>
              ) : (
                <div className="w-7"></div>
              )}
            </div>
            <p className="text-sm text-gray-400 mb-6 text-center">{message}</p>
            {hasInput ? <div className="mb-6"> {inputComponent} </div> : ""}
            <Button
              disabled={isButtonDisabled}
              text={buttonTitle}
              size={"md"}
              onClick={buttonAction}
            />
          </div>
        )}
      </div>
    </div>
  );
};
